import React, { useRef, useState } from 'react';
import { sendContactEmail } from './infra/repository/SendContactEmail';
import './contact.scss';

const MAX_EMAIL_LENGTH = 254;
const MAX_NAME_LENGTH = 100;
const MAX_MESSAGE_LENGTH = 5000;
const EMPTY_FORM = { email: '', name: '', message: '' };

export default function Contact() {
  const [values, setValues] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const sending = useRef(false);

  function update(event) {
    const { name, value } = event.target;
    setValues(previous => ({ ...previous, [name]: value }));
    setErrors(previous => ({ ...previous, [name]: undefined }));
    if (status !== 'sending') setStatus('idle');
  }

  async function submit(event) {
    event.preventDefault();
    if (sending.current) return;
    const form = event.currentTarget;
    const nextErrors = {};
    if (!values.email.trim() || !form.elements.email.validity.valid) nextErrors.email = 'Enter a valid email address.';
    if (!values.name.trim()) nextErrors.name = 'Enter your name.';
    if (!values.message.trim()) nextErrors.message = 'Enter a message.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      form.elements[Object.keys(nextErrors)[0]].focus();
      return;
    }
    sending.current = true;
    setStatus('sending');
    try {
      await sendContactEmail(values);
      setValues(EMPTY_FORM);
      setStatus('success');
    } catch {
      setStatus('error');
    } finally {
      sending.current = false;
    }
  }

  return (
    <main className="contact">
      <h1>Contact</h1>
      <p>Have a question or an idea for Openlexia? Send us a message.</p>
      <form className="contact__form" onSubmit={submit} noValidate aria-busy={status === 'sending'}>
        <div className="contact__field">
          <label htmlFor="contact-email">Email</label>
          <input id="contact-email" name="email" type="email" autoComplete="email" required maxLength={MAX_EMAIL_LENGTH}
            value={values.email} onChange={update} disabled={status === 'sending'}
            aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'contact-email-error' : undefined} />
          {errors.email && <p id="contact-email-error" className="contact__error">{errors.email}</p>}
        </div>
        <div className="contact__field">
          <label htmlFor="contact-name">Name</label>
          <input id="contact-name" name="name" autoComplete="name" required maxLength={MAX_NAME_LENGTH}
            value={values.name} onChange={update} disabled={status === 'sending'}
            aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'contact-name-error' : undefined} />
          {errors.name && <p id="contact-name-error" className="contact__error">{errors.name}</p>}
        </div>
        <div className="contact__field">
          <label htmlFor="contact-message">Message</label>
          <textarea id="contact-message" name="message" required maxLength={MAX_MESSAGE_LENGTH}
            value={values.message} onChange={update} disabled={status === 'sending'}
            aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? 'contact-message-error' : undefined} />
          {errors.message && <p id="contact-message-error" className="contact__error">{errors.message}</p>}
        </div>
        <button className="contact__send" type="submit" disabled={status === 'sending'}>
          {status === 'sending' ? 'Sending…' : 'Send'}
        </button>
        <output>
          {status === 'sending' && 'Sending your message…'}
          {status === 'success' && 'Thank you! Your message has been sent.'}
        </output>
        {status === 'error' && <p className="contact__error" role="alert">Your message could not be sent. Please try again later. Your text has been kept.</p>}
      </form>
    </main>
  );
}
